import { useCallback, useEffect, useState } from 'react';
import type { AdminDashboardStats } from '../../../domain/superadmin/types';
import type { DateRange } from '../../../components/shared/filters/DateRangePicker';
import { useAppContext } from '../../../contexts/AppContext';
import { fetchAdminDashboardStats } from '../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { notify } from '../../../utils/toast';

function defaultRange(): DateRange {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date();
  start.setMonth(start.getMonth() - 12);
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

export function useAdminStats() {
  const [dateRange, setDateRange] = useState<DateRange>(defaultRange);
  const [data, setData] = useState<AdminDashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { setCurrentPage } = useAppContext();

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const hasRange = Boolean(dateRange.start && dateRange.end);
      const stats = await fetchAdminDashboardStats(
        hasRange
          ? {
              from: dateRange.start!.toISOString(),
              to: dateRange.end!.toISOString(),
            }
          : undefined,
      );
      setData(stats);
    } catch (e) {
      if (e instanceof SuperadminApiError && e.isForbidden) {
        setCurrentPage('dashboard');
        notify.warning('Acesso não autorizado à área de superadmin.');
        return;
      }
      setError(e instanceof Error ? e.message : 'Erro ao carregar stats');
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, setCurrentPage]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, isLoading, error, refetch: load, dateRange, setDateRange };
}
